import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Badge, Card } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import KeyModule from './KeyModule';

interface ApiResponse<T> {
  status: string;
  statusDescription: string;
  data: T;
}

export interface RoomDTO {
  id: number;
  name: string;
  floorId: number;
  buildingId: number;
  status: 'ACTIVE' | 'INACTIVE' | 'ONGOING_REPAIR' | string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  description?: string | null;
  numberOfDoors?: number | null;
}

interface DoorDTO {
  id: number;
  name: string;
  roomId: number;
  doorType?: string;
  description?: string | null;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface KeyDTO {
  id: string;
  keyCode: string;
  doorId: string;
  roomId: string;
  vehicleId: string | null;
  noOfKeys: number;
  separatedKeys: number | null;
  bundles: { count: number }[] | null;
  responsibleParty: string | null;
  isRestricted: boolean;
  handoverStatus: string | null;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface ViewKeysModuleProps {
  show: boolean;
  onHide: () => void;
  onUpdated?: () => void;
  response?: ApiResponse<RoomDTO> | null;
  room?: RoomDTO | null;
}

const statusVariant = (s: string) => {
  switch (s) {
    case 'ACTIVE': return 'success';
    case 'INACTIVE': return 'secondary';
    case 'ONGOING_REPAIR': return 'warning';
    default: return 'info';
  }
};

const getDoorCardClass = (keyCount: number) => {
  if (keyCount === 0) return 'bg-danger bg-opacity-25 border-danger';
  if (keyCount <= 5) return 'bg-warning bg-opacity-25 border-warning';
  return 'bg-success bg-opacity-25 border-success';
};

const formatDateTime = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '-');

const ViewKeysModule: React.FC<ViewKeysModuleProps> = ({
  show,
  onHide,
  onUpdated,
  response,
  room,
}) => {
  const rawRoom = response?.data ?? room ?? null;
  const [doors, setDoors] = useState<DoorDTO[]>([]);
  const [keyCounts, setKeyCounts] = useState<Record<number, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDoorId, setSelectedDoorId] = useState<number | null>(null);
  const [showKeyModule, setShowKeyModule] = useState(false);

  const fetchDoorsAndKeys = async () => {
    if (!rawRoom) {
      showError('No room data available.');
      return;
    }
    if (!checkPermission('ROLE_VIEW_DOOR')) {
      showError('You do not have permission to view doors.');
      return;
    }
    setIsLoading(true);
    try {
      const res = await apiDefinitions.getDoorsByRoomId(rawRoom.id);
      if (res.status === 200) {
        const doorData: DoorDTO[] = res.data.data || [];
        setDoors(doorData);

        if (!checkPermission('ROLE_GET_KEYS')) {
          showError('You do not have permission to view keys.');
          setKeyCounts(doorData.reduce((acc, door) => ({ ...acc, [door.id]: 0 }), {}));
          setIsLoading(false);
          return;
        }

        const keyPromises = doorData.map(door =>
          apiDefinitions.getKeysByDoorId(door.id)
            .then(keyRes => {
              if (keyRes.status === 200) {
                const keys: KeyDTO[] = keyRes.data.data || [];
                const doorKeyCount = keys.reduce((sum, key) => sum + (key.noOfKeys || 0), 0);
                return { doorId: door.id, count: doorKeyCount };
              }
              return { doorId: door.id, count: 0 };
            })
            .catch(e => {
              return { doorId: door.id, count: 0 };
            })
        );
        const keyResults = await Promise.all(keyPromises);
        const keyCountMap = keyResults.reduce((acc, { doorId, count }) => ({ ...acc, [doorId]: count }), {});
        setKeyCounts(keyCountMap);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch doors');
      }
    } catch (e: any) {
      showError('Failed to fetch doors. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show && rawRoom) fetchDoorsAndKeys();
  }, [show, rawRoom]);

  const handleDoorClick = (doorId: number) => {
    setSelectedDoorId(doorId);
    setShowKeyModule(true);
  };

  if (!rawRoom) {
    return (
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            View Room: {rawRoom.name}{' '}
            <Badge bg={statusVariant(rawRoom.status)} className="ms-2">
              {rawRoom.status}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="roomId">
                  <Form.Label>ID</Form.Label>
                  <Form.Control type="text" value={rawRoom.id} disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="roomName">
                  <Form.Label>Name</Form.Label>
                  <Form.Control type="text" value={rawRoom.name} disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="status">
                  <Form.Label>Status</Form.Label>
                  <Form.Control type="text" value={rawRoom.status} disabled />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Doors</Form.Label>
                <style>{`
                  .door-chip {
                    min-width: 160px;
                    max-width: 220px;
                    border-radius: .85rem;
                    transition: transform .12s ease, filter .12s ease, box-shadow .12s ease;
                    cursor: pointer;
                  }
                  .door-chip:hover {
                    transform: translateY(-1px);
                    filter: brightness(1.05);
                    box-shadow: 0 6px 16px rgba(0,0,0,.08);
                  }
                  .door-chip__name {
                    font-size: .95rem;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 60%;
                    color: #212529;
                  }
                  .door-chip__badge {
                    font-size: .8rem;
                    padding: .25rem .5rem;
                  }
                `}</style>
                {isLoading ? (
                  <div className="text-center">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading doors...</span>
                    </Spinner>
                  </div>
                ) : doors.length === 0 ? (
                  <div className="text-muted">No doors available for this room.</div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {doors.map((door) => (
                      <Card
                        key={door.id}
                        role="button"
                        onClick={() => handleDoorClick(door.id)}
                        title={door.name}
                        className={`door-chip ${getDoorCardClass(keyCounts[door.id] || 0)} border-0`}
                      >
                        <Card.Body className="py-2 px-3 d-flex justify-content-between align-items-center">
                          <span className="door-chip__name fw-semibold">
                            {door.name}
                          </span>
                          <Badge
                            bg="light"
                            text="dark"
                            className="door-chip__badge rounded-pill"
                            title={`${keyCounts[door.id] || 0} key(s)`}
                          >
                            <i className="feather-key me-1"></i>
                            {keyCounts[door.id] || 0}
                          </Badge>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="numberOfDoors">
                  <Form.Label>Number of Doors</Form.Label>
                  <Form.Control
                    type="text"
                    value={rawRoom.numberOfDoors !== undefined && rawRoom.numberOfDoors !== null
                      ? rawRoom.numberOfDoors
                      : '-'}
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="floorId">
                  <Form.Label>Floor ID</Form.Label>
                  <Form.Control type="text" value={rawRoom.floorId} disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="buildingId">
                  <Form.Label>Building ID</Form.Label>
                  <Form.Control type="text" value={rawRoom.buildingId} disabled />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="description">
                  <Form.Label>Description</Form.Label>
                  <Form.Control as="textarea" rows={2} value={rawRoom.description ?? '-'} disabled />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group controlId="createdBy">
                  <Form.Label>Created By</Form.Label>
                  <Form.Control type="text" value={rawRoom.createdBy} disabled />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="updatedBy">
                  <Form.Label>Updated By</Form.Label>
                  <Form.Control type="text" value={rawRoom.updatedBy ?? '-'} disabled />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="createdAt">
                  <Form.Label>Created At</Form.Label>
                  <Form.Control type="text" value={formatDateTime(rawRoom.createdAt)} disabled />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="updatedAt">
                  <Form.Label>Updated At</Form.Label>
                  <Form.Control type="text" value={formatDateTime(rawRoom.updatedAt)} disabled />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-3">
              <Button variant="primary" onClick={onHide} disabled={isLoading}>
                Close
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {selectedDoorId && (
        <KeyModule
          show={showKeyModule}
          onHide={() => {
            setShowKeyModule(false);
            setSelectedDoorId(null);
          }}
          doorId={selectedDoorId}
          roomId={rawRoom.id}
          onKeyAdded={() => {
            fetchDoorsAndKeys();
            onUpdated?.();
          }}
        />
      )}
    </>
  );
};

export default ViewKeysModule;